import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowBigUp,
  Bookmark,
  BookOpen,
  BrainCircuit,
  CheckCircle2,
  ChevronDown,
  CircleAlert,
  Clock3,
  FileText,
  Flag,
  Home,
  Loader2,
  LogOut,
  MessageCircle,
  Pencil,
  Plus,
  Settings,
  ShieldCheck,
  Sparkles,
  Upload,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { enUS, zhCN } from 'date-fns/locale';
import { api, ApiError, postJson } from './api';
import { RichText } from './RichText';
import { PASSWORD_LENGTH_HELP, passwordLengthMeetsPolicy } from '../shared/password-policy';

export type User = { id: string; email: string; name: string; avatarUrl: string | null; role: string; plan: string };
type Session = { user: User | null; billingEnabled: boolean };
type ReplyJob = { id: string; job_type: 'reply_post' | 'reply_comment'; target_id: string; status: 'queued' | 'running' | 'succeeded' | 'failed'; attempts: number; updated_at: string };
type Citation = { target_id?: string; chunk_id: string; quote: string; material_name: string };
type Persona = {
  id: string;
  role_key: string;
  name: string;
  tagline: string;
  tone: string;
  avatar_seed: string;
  system_prompt?: string;
};
type Post = {
  id: string;
  group_id: string;
  title: string;
  body: string;
  source_type: 'human' | 'ai' | 'system';
  score: number;
  comment_count: number;
  created_at: string;
  user_name?: string;
  user_avatar?: string;
  persona_name?: string;
  persona_tagline?: string;
  role_key?: string;
  avatar_seed?: string;
  upvoted?: number;
  bookmarked?: number;
  knowledge_title?: string;
  has_sample_answer?: number;
  sample_answer?: string;
  citations: Citation[];
};
type Comment = {
  id: string;
  parent_id?: string | null;
  body: string;
  source_type: string;
  depth: number;
  score: number;
  upvoted?: boolean | number;
  created_at: string;
  user_name?: string;
  user_avatar?: string;
  persona_name?: string;
  persona_tagline?: string;
  role_key?: string;
  avatar_seed?: string;
  citations: Citation[];
};
type GroupMeta = { group: any; personas: Persona[]; materials: any[]; knowledge: any[] };

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const refreshSession = useCallback(
    () => api<Session>('/api/auth/session').then(setSession).catch(() => setSession({ user: null, billingEnabled: false })),
    [],
  );
  useEffect(() => { refreshSession(); }, [refreshSession]);
  if (!session) return <FullScreenLoader label="正在进入 FeedDive" />;
  return (
    <Routes>
      <Route path="/" element={<Landing user={session.user} />} />
      <Route path="/privacy" element={<LegalPage kind="privacy" />} />
      <Route path="/terms" element={<LegalPage kind="terms" />} />
      <Route path="/acceptable-use" element={<LegalPage kind="acceptable" />} />
      <Route path="/support" element={<LegalPage kind="support" />} />
      <Route path="/login" element={session.user ? <Navigate to="/app" replace /> : <Login onLogin={refreshSession} />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route element={<Protected user={session.user} />}>
        <Route path="/app" element={<Shell user={session.user!} onLogout={refreshSession}><Dashboard /></Shell>} />
        <Route path="/app/new" element={<Shell user={session.user!} onLogout={refreshSession}><CreateGroup /></Shell>} />
        <Route path="/app/billing" element={<Shell user={session.user!} onLogout={refreshSession}><Billing user={session.user!} enabled={session.billingEnabled} /></Shell>} />
        <Route path="/app/settings" element={<Shell user={session.user!} onLogout={refreshSession}><AccountSettings user={session.user!} onLogout={refreshSession} /></Shell>} />
        <Route path="/app/admin/reports" element={<Shell user={session.user!} onLogout={refreshSession}><AdminReports user={session.user!} /></Shell>} />
        <Route path="/g/:id" element={<Shell user={session.user!} onLogout={refreshSession}><GroupFeed /></Shell>} />
        <Route path="/p/:id" element={<Shell user={session.user!} onLogout={refreshSession}><PostPage user={session.user!} /></Shell>} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function Protected({ user }: { user: User | null }) {
  return user ? <Outlet /> : <Navigate to="/login" replace />;
}

function Landing({ user }: { user: User | null }) {
  return (
    <div className="landing">
      <header className="landing-nav"><Brand /><nav><a href="#how">工作方式</a><a href="#modes">两种模式</a><a href="/pricing.html">定价</a><Link className="button ghost" to={user ? '/app' : '/login'}>{user ? '进入小组' : '登录'}</Link></nav></header>
      <main>
        <section className="hero">
          <div className="hero-copy">
            <div className="eyebrow"><Sparkles size={16} /> 不是摘要，是一个会回应你的知识社区</div>
            <h1>Scroll down.<br /><span>Dive deep.</span></h1>
            <p>上传任何材料，把它变成由教授、怀疑者、工程师、同伴、评审及自定义的各种视角角色共同参与的无限向下滚动深度讨论信息流。</p>
            <div className="hero-actions"><Link className="button primary large" to={user ? '/app/new' : '/login'}>创建第一个小组 <Plus size={18} /></Link><a className="button ghost large" href="#how">看看如何工作</a></div>
            <div className="trust-row"><ShieldCheck size={17} /> 访问受限存储 · 来源引用 · 可删除 · 上传前说明外部 AI 处理</div>
          </div>
          <div className="hero-feed">
            <MockPost seed="0" role="发现搞笑点的人" title="等等，这篇材料最幽默的地方难道不是第 3 节那句“显然成立”吗？" body="推导写得越严肃，看到“忽略所有实际摩擦和异常情况”时就越想笑，放在真实世界里直接当场宕机。" score={128} />
            <MockPost seed="1" role="实践派工程师" title="把这个概念放进生产环境，最先坏的是哪一层？" body="理论没有错，真正麻烦的是数据漂移、观测和回滚。" score={86} />
            <MockPost seed="3" role="面试官" title="不用术语，你能在 30 秒内解释它吗？" body="先给直觉，再给严格条件，最后说一个反例。" score={54} />
          </div>
        </section>
        <section id="how" className="section"><div className="section-heading"><span>工作方式</span><h2>材料进入，讨论开始</h2></div><div className="steps"><Step n="01" icon={<Upload />} title="上传材料" text="粘贴文本，或上传 PDF、DOCX、XLSX、PPTX、图片和音频。" /><Step n="02" icon={<BrainCircuit />} title="拆解知识" text="提取知识点、建立引用，并生成不同角色。" /><Step n="03" icon={<MessageCircle />} title="刷帖与答辩" text="每条帖子都会形成多角色讨论；真人发言继续触发针对性回应。" /></div></section>
        <section id="modes" className="section split"><ModeCard type="学习模式" title="把教材变成会追问的 Feed" items={['直觉解释与常见误区', '工程案例与反例', '掌握状态与复习时间']} /><ModeCard type="评审模式" title="把汇报变成提前发生的答辩" items={['多角色质疑和表扬', '附带参考示例回答', 'Defend 后继续追问']} /></section>
      </main>
      <footer><Brand /><span>上传材料，创建一个会思考的知识小组。</span><nav><a href="/pricing.html">定价</a><Link to="/privacy">隐私</Link><Link to="/terms">条款</Link><Link to="/acceptable-use">可接受使用</Link><Link to="/support">支持</Link></nav></footer>
    </div>
  );
}

function Login({ onLogin }: { onLogin: () => Promise<void> | void }) {
  const [mode, setMode] = useState<'login' | 'register' | 'forgot' | 'verify'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const needsPassword = mode === 'login' || mode === 'register';
  function switchMode(next: typeof mode) {
    setMode(next);
    setPassword('');
    setError('');
    setNotice('');
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (mode === 'register' && !passwordLengthMeetsPolicy(password)) {
      setError(PASSWORD_LENGTH_HELP);
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      if (mode === 'forgot') {
        await postJson('/api/auth/forgot-password', { email });
        setNotice('如果该邮箱已注册，我们会发送一封密码重置邮件。');
        return;
      }
      if (mode === 'verify') {
        await postJson('/api/auth/resend-verification', { email });
        setNotice('如果账号尚未验证，我们会重新发送验证邮件。');
        return;
      }
      if (mode === 'register') {
        await postJson('/api/auth/register', { email, password });
        switchMode('verify');
        setNotice('账号已创建。请打开邮箱完成验证后再登录。');
        return;
      }
      await postJson('/api/auth/login', { email, password });
      await onLogin();
      navigate('/app');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'EMAIL_NOT_VERIFIED') {
        switchMode('verify');
        setNotice('这个账号还没有验证邮箱。');
      } else setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }
  const title = mode === 'register' ? '创建账号' : mode === 'forgot' ? '忘记密码' : mode === 'verify' ? '验证邮箱' : '邮箱登录';
  const description = mode === 'register' ? '注册后，我们会向这个邮箱发送验证链接。' : mode === 'forgot' ? '输入注册邮箱，我们会发送密码重置链接。' : mode === 'verify' ? '没有收到邮件？可以重新发送验证链接。' : '继续你的知识小组，或创建新的材料讨论。';
  const action = mode === 'register' ? '创建账号' : mode === 'forgot' ? '发送重置邮件' : mode === 'verify' ? '重新发送验证邮件' : '登录';
  return <div className="auth-page"><Link to="/" className="auth-brand"><Brand /></Link><div className="auth-card"><div className="auth-icon"><BrainCircuit /></div><h1>{title}</h1><p>{description}</p><form onSubmit={submit} className="stack"><label>邮箱<input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" required /></label>{needsPassword && <label>密码<input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete={mode === 'register' ? 'new-password' : 'current-password'} required /></label>}{mode === 'register' && <span className="muted">{PASSWORD_LENGTH_HELP}</span>}{notice && <div className="notice" role="status"><CheckCircle2 size={18} />{notice}</div>}{error && <div className="form-error">{error}</div>}<button className="button primary full" disabled={busy}>{busy ? <Loader2 className="spin" /> : action}</button></form>{mode === 'login' ? <p className="auth-note">还没有账号？ <button type="button" className="text-button" onClick={() => switchMode('register')}>立即注册</button> · <button type="button" className="text-button" onClick={() => switchMode('forgot')}>忘记密码</button></p> : <p className="auth-note"><button type="button" className="text-button" onClick={() => switchMode('login')}>返回邮箱登录</button></p>}<p className="auth-note">登录或注册即表示你同意 <Link to="/terms">服务条款</Link> 与 <Link to="/privacy">隐私说明</Link>；具体材料处理会在上传前再次确认。</p></div></div>;
}

function VerifyEmail() {
  const token = new URLSearchParams(useLocation().search).get('token') || '';
  const [status, setStatus] = useState<'idle' | 'busy' | 'done'>('idle');
  const [error, setError] = useState(token ? '' : '验证链接不完整。');
  async function verify() {
    setStatus('busy');
    setError('');
    try {
      await postJson('/api/auth/verify-email', { token });
      setStatus('done');
    } catch (err) {
      setError(messageOf(err));
      setStatus('idle');
    }
  }
  return <div className="auth-page"><Link to="/" className="auth-brand"><Brand /></Link><div className="auth-card"><div className="auth-icon"><ShieldCheck /></div><h1>{status === 'done' ? '邮箱验证成功' : '确认验证邮箱'}</h1><p>{status === 'done' ? '现在可以使用邮箱和密码登录。' : '点击下面的按钮完成验证。验证链接只能使用一次。'}</p>{error && <div className="form-error">{error}</div>}{status === 'done' ? <Link className="button primary full" to="/login">返回登录</Link> : <button className="button primary full" onClick={verify} disabled={!token || status === 'busy'}>{status === 'busy' ? <Loader2 className="spin" /> : '验证邮箱'}</button>}</div></div>;
}

function ResetPassword() {
  const token = new URLSearchParams(useLocation().search).get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(token ? '' : '重置链接不完整。');
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirmation) return setError('两次输入的密码不一致。');
    if (!passwordLengthMeetsPolicy(password)) return setError(PASSWORD_LENGTH_HELP);
    setBusy(true);
    setError('');
    try {
      await postJson('/api/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }
  return <div className="auth-page"><Link to="/" className="auth-brand"><Brand /></Link><div className="auth-card"><div className="auth-icon"><ShieldCheck /></div><h1>{done ? '密码已重置' : '设置新密码'}</h1><p>{done ? '旧会话和其他重置链接已经失效。' : PASSWORD_LENGTH_HELP}</p>{done ? <Link className="button primary full" to="/login">使用新密码登录</Link> : <form className="stack" onSubmit={submit}><label>新密码<input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="new-password" required /></label><label>再次输入<input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} type="password" autoComplete="new-password" required /></label>{error && <div className="form-error">{error}</div>}<button className="button primary full" disabled={!token || busy}>{busy ? <Loader2 className="spin" /> : '重置密码'}</button></form>}</div></div>;
}

function Shell({ user, onLogout, children }: { user: User; onLogout: () => Promise<void> | void; children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  async function logout() {
    await postJson('/api/auth/logout', {});
    await onLogout();
    navigate('/');
  }
  return <div className="app-shell"><header className="topbar"><Link to="/app"><Brand /></Link><div /><div className="top-actions"><Link to="/app/new" className="button primary compact"><Plus size={16} /> 创建</Link><button className="avatar-button" onClick={() => setMobileOpen(!mobileOpen)}><Avatar name={user.name} src={user.avatarUrl} seed={user.id || user.name} /></button></div></header><aside className={`sidebar ${mobileOpen ? 'open' : ''}`}><NavLink to="/app" active={location.pathname === '/app'} icon={<Home />}>主页</NavLink><NavLink to="/app/new" active={location.pathname === '/app/new'} icon={<Plus />}>创建小组</NavLink><div className="nav-label">账户</div><NavLink to="/app/billing" active={location.pathname.includes('billing')} icon={<Wallet />}>订阅与用量</NavLink><NavLink to="/app/settings" active={location.pathname.includes('settings')} icon={<Settings />}>设置</NavLink>{user.role === 'admin' && <NavLink to="/app/admin/reports" active={location.pathname.includes('/admin/reports')} icon={<Flag />}>举报队列</NavLink>}<div className="sidebar-user"><Avatar name={user.name} src={user.avatarUrl} seed={user.id || user.name} /><div><strong>{user.name}</strong><span>{user.plan === 'free' ? 'Free' : 'Pro'} 方案</span></div><button onClick={logout} title="退出"><LogOut size={17} /></button></div></aside><main className="app-main">{children}</main></div>;
}

function Dashboard() {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const load = useCallback(() => api<any>('/api/dashboard').then(setData).catch((err) => setError(messageOf(err))), []);
  useEffect(() => {
    load();
    const timer = setInterval(() => { if (data?.groups?.some((group: any) => group.status === 'processing')) load(); }, 3500);
    return () => clearInterval(timer);
  }, [load, data?.groups]);
  if (error) return <ErrorState message={error} retry={load} />;
  if (!data) return <PageLoader />;
  const usage = data.usage?.ai_calls || 0;
  const max = data.limits.aiCalls;
  return <div className="page"><div className="page-header"><div><div className="eyebrow">你的知识空间</div><h1>继续深入</h1><p>在帖子流里复习、回应和发现薄弱点。</p></div><Link className="button primary" to="/app/new"><Plus size={17} /> 新建小组</Link></div><div className="metric-row"><Metric icon={<Users />} value={data.groups.length} label="小组" /><Metric icon={<MessageCircle />} value={data.groups.reduce((total: number, group: any) => total + Number(group.post_count || 0), 0)} label="帖子" /><Metric icon={<Sparkles />} value={usage} label={max ? `本月 AI / ${max}` : '本月 AI 调用'} /></div>{data.groups.length === 0 ? <EmptyDashboard /> : <div className="group-grid">{data.groups.map((group: any) => <GroupCard key={group.id} group={group} />)}</div>}</div>;
}

function CreateGroup() {
  const [mode, setMode] = useState<'learning' | 'review'>('learning');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const navigate = useNavigate();
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError('');
    const form = new FormData(event.currentTarget);
    form.set('mode', mode);
    if (file) form.set('file', file);
    try {
      const result = await api<{ group: { id: string } }>('/api/groups', { method: 'POST', body: form });
      navigate(`/g/${result.group.id}`);
    } catch (err) {
      setError(messageOf(err));
      setBusy(false);
    }
  }
  return <div className="page narrow"><div className="page-header"><div><div className="eyebrow">创建材料小组</div><h1>你希望这份材料发生什么？</h1><p>先选择讨论方式，再上传或粘贴内容。</p></div></div><form className="create-form" onSubmit={submit}><div className="mode-picker"><button type="button" className={mode === 'learning' ? 'mode active' : 'mode'} onClick={() => setMode('learning')}><BookOpen /><strong>知识学习小组</strong><span>解释、反例、误区、案例和理解测试</span></button><button type="button" className={mode === 'review' ? 'mode active' : 'mode'} onClick={() => setMode('review')}><Flag /><strong>汇报评审小组</strong><span>质疑、风险、表扬、示例回答和追问</span></button></div><div className="form-card"><label>小组标题<input name="title" required minLength={2} maxLength={120} placeholder={mode === 'learning' ? '例如：深度学习课程第二章' : '例如：Q3 产品汇报答辩'} /></label><label>背景说明（可选）<textarea name="description" maxLength={1000} rows={3} placeholder="目标、受众、考试范围或评审背景" /></label><div className="upload-zone"><input id="file" type="file" onChange={(event) => setFile(event.target.files?.[0] || null)} accept=".txt,.md,.csv,.json,.html,.pdf,.docx,.pptx,.xlsx,.xlsm,.xlsb,.xls,.png,.jpg,.jpeg,.webp,.svg,.gif,.bmp,.mp3,.wav,.m4a,.webm,.ogg,.flac,.mp4" /><label htmlFor="file"><Upload /><strong>{file ? file.name : '选择文件'}</strong><span>PDF、DOCX、XLSX、PPTX、图片、音频或文本，最大 10 MB</span></label>{file && <button type="button" className="remove-file" onClick={() => setFile(null)}><X size={16} /></button>}</div><div className="divider"><span>或者直接粘贴</span></div><label>材料文本<textarea name="text" rows={12} minLength={file ? 0 : 50} placeholder="粘贴课程笔记、论文、汇报稿、背景资料或网页内容……" /></label><div className="consent-box"><strong>外部 AI 处理确认</strong><p>材料会由 Cloudflare 解析、转写、建立向量和执行后台任务；生成请求首先发送到 OpenRouter 的 StreamLake 限定路由（DeepSeek V4 Flash 0731，OpenRouter 内部 provider fallback 已禁用）；只有可重试的供应商故障时才由 FeedDive 切换到 DeepInfra。音频仅由 Cloudflare Workers AI Whisper 处理。OpenRouter/StreamLake 可能接收并保留完成生成所需的材料片段，StreamLake 当前不使用 prompts 训练模型且总部在中国；DeepInfra 仅在上述可重试故障时接收同一生成输入。请勿上传机密、受监管或不必要的敏感个人信息。</p><label><input type="checkbox" name="aiProcessingConsent" value="accepted" required />我已阅读 <Link to="/privacy" target="_blank">隐私说明</Link>，同意本次材料按上述路径处理。</label><label><input type="checkbox" name="sensitiveDataConfirmed" value="true" required />我确认已取得上传权限，且材料不含机密、账号密钥、健康/金融等敏感个人信息。</label></div>{error && <div className="form-error">{error}</div>}<button className="button primary large full" disabled={busy}>{busy ? <><Loader2 className="spin" /> 已入队，正在处理</> : <><Sparkles size={18} /> 创建并生成 Feed</>}</button><p className="fine-print">原文件存入访问受限的 R2；解析文本、知识点、帖子和引用存入 D1。选择“重新解析”不会删除真人帖子或真人评论。</p></div></form></div>;
}

function GroupFeed() {
  const { id } = useParams();
  const [meta, setMeta] = useState<GroupMeta | null>(null);
  const [feed, setFeed] = useState<{ posts: Post[]; nextCursor: string | null } | null>(null);
  const [error, setError] = useState('');
  const [composer, setComposer] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [groupMeta, groupFeed] = await Promise.all([api<GroupMeta>(`/api/groups/${id}`), api<{ posts: Post[]; nextCursor: string | null }>(`/api/groups/${id}/feed`)]);
      setMeta(groupMeta);
      setFeed(groupFeed);
      setError('');
    } catch (err) {
      setError(messageOf(err));
    }
  }, [id]);

  const loadMore = useCallback(async () => {
    if (!id || !feed?.nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const more = await api<{ posts: Post[]; nextCursor: string | null }>(`/api/groups/${id}/feed?cursor=${encodeURIComponent(feed.nextCursor)}`);
      setFeed((prev) => prev ? { posts: [...prev.posts, ...more.posts], nextCursor: more.nextCursor } : prev);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setLoadingMore(false);
    }
  }, [id, feed?.nextCursor, loadingMore]);

  const needsInitialDiscussion = useMemo(
    () => Boolean(feed?.posts.some((post) => post.source_type === 'ai' && Number(post.comment_count || 0) < 2)),
    [feed?.posts],
  );
  const needsHumanReplies = useMemo(
    () => Boolean(feed?.posts.some((post) => post.source_type === 'human' && Number(post.comment_count || 0) < 2 && Date.now() - new Date(post.created_at.replace(' ', 'T') + (post.created_at.includes('T') ? '' : 'Z')).getTime() < 10 * 60_000)),
    [feed?.posts],
  );

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!id || meta?.group?.status !== 'ready' || !needsInitialDiscussion) return;
    postJson(`/api/groups/${id}/discussions/ensure`, {}).catch(() => undefined);
  }, [id, meta?.group?.status, needsInitialDiscussion]);
  useEffect(() => {
    if (meta?.group?.status !== 'processing' && !needsInitialDiscussion && !needsHumanReplies) return;
    const timer = setInterval(load, 3000);
    return () => clearInterval(timer);
  }, [load, meta?.group?.status, needsInitialDiscussion, needsHumanReplies]);

  if (error) return <ErrorState message={error} retry={load} />;
  if (!meta || !feed) return <PageLoader />;
  if (meta.group.status === 'processing') return <ProcessingState title={meta.group.title} mode={meta.group.mode} />;
  if (meta.group.status === 'failed') return <ErrorState message={meta.group.generation_error || '生成失败'} retry={async () => { await postJson(`/api/groups/${id}/retry`, {}); await load(); }} />;

  return <div className="feed-layout"><section className="feed-column"><div className="community-header"><div className={`community-icon ${meta.group.mode}`}>{meta.group.mode === 'review' ? <Flag /> : <BookOpen />}</div><div><div className="community-path">f/{meta.group.slug}</div><h1>{meta.group.title}</h1><p>{meta.group.description || `${meta.personas.length} 个 AI 角色正在围绕材料讨论`}</p>{(needsInitialDiscussion || needsHumanReplies) && <span className="discussion-pending"><Loader2 className="spin" /> 角色正在补充回复</span>}</div><button className="button primary compact" onClick={() => setComposer(!composer)}><Plus size={16} /> 发帖</button></div>{composer && <PostComposer groupId={id!} onDone={() => { setComposer(false); load(); }} />}{feed.posts.length === 0 ? <div className="empty-card"><Loader2 className="spin" /><h3>正在生成第一批帖子</h3><p>稍后自动刷新。</p></div> : <>{feed.posts.map((post) => <PostCard key={post.id} post={post} onUpdate={load} />)}{feed.nextCursor && <button className="button ghost load-more" onClick={loadMore} disabled={loadingMore}>{loadingMore ? <Loader2 className="spin" size={16} /> : <ChevronDown size={16} />} 加载更多</button>}</>}</section><aside className="right-rail"><AboutGroup meta={meta} onUpdated={load} /><KnowledgePanel items={meta.knowledge} /></aside></div>;
}

function PostPage({ user }: { user: User }) {
  const { id } = useParams();
  const [data, setData] = useState<{ post: Post; comments: Comment[]; replyJobs: ReplyJob[] } | null>(null);
  const [error, setError] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const commentInput = useRef<HTMLTextAreaElement>(null);
  const load = useCallback(() => id ? api<{ post: Post; comments: Comment[]; replyJobs: ReplyJob[] }>(`/api/posts/${id}`).then((result) => { setData(result); setError(''); }).catch((err) => setError(messageOf(err))) : Promise.resolve(), [id]);
  const hasPendingJobs = Boolean(data?.replyJobs.some((job) => job.status === 'queued' || job.status === 'running' || job.status === 'failed'));
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!hasPendingJobs) return;
    const timer = setInterval(load, 3500);
    return () => clearInterval(timer);
  }, [load, hasPendingJobs]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!id || !body.trim()) return;
    setBusy(true);
    try {
      await postJson(`/api/posts/${id}/comments`, { body, parentId: replyTo });
      setBody('');
      setReplyTo(null);
      await load();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }
  if (error && !data) return <ErrorState message={error} retry={load} />;
  if (!data) return <PageLoader />;
  const tree = buildCommentTree(data.comments);
  const replyTarget = replyTo ? data.comments.find((comment) => comment.id === replyTo) : null;
  const replyTargetName = replyTarget?.persona_name || replyTarget?.user_name || '这条评论';
  const failedJobs = data.replyJobs.filter((job) => job.status === 'failed');
  const pendingJobs = data.replyJobs.filter((job) => job.status === 'queued' || job.status === 'running');
  function startReply(commentId: string) {
    setReplyTo(commentId);
    commentInput.current?.focus();
    commentInput.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  async function retryFailed() {
    setBusy(true);
    try {
      await Promise.all(failedJobs.map((job) => postJson(`/api/ai-jobs/${job.id}/retry`, {})));
      await load();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }
  return <div className="post-layout"><section><Link className="back-link" to={`/g/${data.post.group_id}`}>← 返回小组</Link><PostCard post={data.post} detail onUpdate={load} />{(pendingJobs.length > 0 || failedJobs.length > 0) && <div className={`reply-job-state ${failedJobs.length ? 'failed' : ''}`}>{failedJobs.length ? <CircleAlert /> : <Loader2 className="spin" />}<span>{failedJobs.length ? `${failedJobs.length} 条 AI 回复暂时失败，真人内容已安全保存。` : `${pendingJobs.length} 条 AI 回复正在生成。`}</span>{failedJobs.length > 0 && <button className="button ghost compact" onClick={retryFailed} disabled={busy}>重试回复</button>}</div>}<div className="comment-composer"><Avatar name={user.name} src={user.avatarUrl} seed={user.id || user.name} /><form onSubmit={submit}><textarea ref={commentInput} value={body} onChange={(event) => setBody(event.target.value)} maxLength={6000} placeholder={replyTo ? `回复 @${replyTargetName}。AI 会继续跟进这条对话。` : '加入讨论。AI 会选择最适合的角色回应你。'} rows={4} /><div className="composer-footer">{replyTo && <button type="button" className="text-button" onClick={() => setReplyTo(null)}>正在回复 @{replyTargetName} · 取消</button>}<button className="button primary compact" disabled={busy || !body.trim()}>{busy ? <Loader2 className="spin" /> : '回复'}</button></div></form></div><div className="comments-head"><strong>{data.comments.length} 条评论</strong><span>每条真人发言均建立独立 AI 回复任务；失败时可安全重试</span></div><div className="comment-list">{tree.map((node) => <CommentNode key={node.item.id} node={node} onReply={startReply} onUpdate={load} />)}</div></section><aside className="right-rail"><div className="rail-card"><h3>讨论规则</h3><ul><li>攻击观点，不攻击人</li><li>事实结论尽量查看材料引用</li><li>用反例检验，而不是只表示赞同</li></ul></div></aside></div>;
}

function PostCard({ post, detail = false, onUpdate }: { post: Post; detail?: boolean; onUpdate: () => void }) {
  const [sample, setSample] = useState(true);
  const author = post.persona_name || post.user_name || 'FeedDive';
  const isAi = post.source_type === 'ai';
  async function interact(kind: 'upvote' | 'bookmark' | 'hide', active: boolean) {
    await postJson('/api/interactions', { targetType: 'post', targetId: post.id, kind, active });
    onUpdate();
  }
  return (
    <article className={`post-card ${detail ? 'detail' : ''}`}>
      <div className="vote-rail">
        <button className={post.upvoted ? 'active' : ''} onClick={() => interact('upvote', !post.upvoted)}>
          <ArrowBigUp />
        </button>
        <strong>{post.score}</strong>
      </div>
      <div className="post-content">
        <div className="post-meta">
          <Avatar name={author} seed={post.avatar_seed} src={post.user_avatar} />
          <span className="author">{author}</span>
          {isAi && <span className="ai-badge"><Sparkles size={12} /> AI</span>}
          <span>·</span>
          <span>{timeAgo(post.created_at)}</span>
          {post.persona_tagline && <span className="persona-tagline">{post.persona_tagline}</span>}
        </div>
        {detail ? <h1><RichText text={post.title} /></h1> : <Link to={`/p/${post.id}`}><h2><RichText text={post.title} /></h2></Link>}
        <RichText as="p" className="post-body" text={post.body} />
        {post.citations?.length > 0 && <CitationList citations={post.citations} />}
        {post.has_sample_answer && post.sample_answer ? (
          <div className="sample-wrap">
            <div className="sample-header">
              <span className="sample-badge"><Sparkles size={13} /> 示例参考回答</span>
              <button className="sample-toggle" onClick={() => setSample(!sample)}>
                <ChevronDown className={sample ? 'rotated' : ''} /> {sample ? '收起' : '展开'}
              </button>
            </div>
            {sample && (
              <div className="sample-answer">
                <RichText as="p" text={post.sample_answer} />
              </div>
            )}
          </div>
        ) : null}
        <div className="post-actions">
          <Link to={`/p/${post.id}`}><MessageCircle size={16} />{post.comment_count} 评论</Link>
          <button onClick={() => interact('bookmark', !post.bookmarked)} className={post.bookmarked ? 'active' : ''}><Bookmark size={16} />{post.bookmarked ? '已收藏' : '收藏'}</button>
          <button onClick={() => interact('hide', true)}><X size={16} />隐藏</button>
          <button onClick={() => reportContent('post', post.id)}><Flag size={15} />举报</button>
        </div>
      </div>
    </article>
  );
}

function PostComposer({ groupId, onDone }: { groupId: string; onDone: () => void }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await postJson(`/api/groups/${groupId}/posts`, { title, body });
      onDone();
    } catch (err) {
      setError(messageOf(err));
      setBusy(false);
    }
  }
  return <form className="post-composer" onSubmit={submit}><input value={title} onChange={(event) => setTitle(event.target.value)} minLength={4} maxLength={220} required placeholder="你想发起什么讨论？" /><textarea value={body} onChange={(event) => setBody(event.target.value)} minLength={1} maxLength={10000} required rows={5} placeholder="写下观点、困惑、回答或反例。支持 $...$ 和 $$...$$ 公式。" />{error && <div className="form-error">{error}</div>}<div className="composer-footer"><span>发布后至少两个不同 AI 角色会给出回应或解答</span><button className="button primary compact" disabled={busy}>{busy ? <Loader2 className="spin" /> : '发布'}</button></div></form>;
}

function Billing({ user, enabled }: { user: User; enabled: boolean }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const canCheckout = enabled && (user.plan === 'free' || user.role === 'admin');
  async function checkout(plan: 'pro' | 'premium') {
    setBusy(plan); setError('');
    try {
      const { url } = await postJson<{ url: string }>('/api/billing/checkout', { plan });
      window.location.assign(url);
    } catch (err) { setError(messageOf(err)); setBusy(''); }
  }
  async function manage() {
    setBusy('portal'); setError('');
    try {
      const { url } = await postJson<{ url: string }>('/api/billing/portal', {});
      window.location.assign(url);
    } catch (err) { setError(messageOf(err)); setBusy(''); }
  }
  const currentName = user.plan === 'premium' ? 'Premium' : user.plan === 'pro' ? 'Pro' : 'Free';
  return <div className="page narrow"><div className="page-header"><div><div className="eyebrow">订阅与用量</div><h1>{user.plan === 'free' ? '升级你的知识空间' : `你正在使用 ${currentName}`}</h1><p>启用后由 Dodo Payments 负责结账、税务、发票和订阅状态。</p></div></div>{!enabled && <div className="notice"><CircleAlert size={18} />Dodo Payments Live 仍处于审核阶段；价格已公开，当前不接受真实付款。</div>}{error && <div className="form-error">{error}</div>}{enabled && user.plan !== 'free' && <button className="button primary" onClick={manage} disabled={Boolean(busy)}>{busy === 'portal' ? <Loader2 className="spin" /> : '管理或取消订阅'}</button>}<div className="pricing-grid"><div className="price-card"><span>Free</span><h2>$0<span className="price-unit">/月</span></h2><p>验证核心学习循环</p><ul><li>1 个访问受限小组</li><li>每月 120 次 AI 调用</li><li>完整帖子与评论</li></ul></div><div className="price-card"><span>Pro</span><h2>$9.99<span className="price-unit">/月</span></h2><p>{enabled ? '按月订阅，最终金额以 Dodo Payments 结账页为准。' : 'Dodo Payments 审核完成并单独启用后开放真实付款。'}</p><ul><li>最多 10 个小组</li><li>每月 600 次 AI 调用</li><li>邮件通知</li><li>个性化复习与评审</li></ul>{canCheckout && <button className="button primary full" onClick={() => checkout('pro')} disabled={Boolean(busy)}>{busy === 'pro' ? <Loader2 className="spin" /> : 'Pro $9.99/月'}</button>}</div><div className="price-card featured"><div className="popular">推荐</div><span>Premium</span><h2>$19.99<span className="price-unit">/月</span></h2><p>{enabled ? '按月订阅，最终金额以 Dodo Payments 结账页为准。' : 'Dodo Payments 审核完成并单独启用后开放真实付款。'}</p><ul><li>无限小组数量</li><li>每月 1200 次 AI 调用</li><li>优先生成与邮件通知</li><li>完整个性化复习与评审</li></ul>{canCheckout && <button className="button primary full" onClick={() => checkout('premium')} disabled={Boolean(busy)}>{busy === 'premium' ? <Loader2 className="spin" /> : 'Premium $19.99/月'}</button>}</div></div></div>;
}

function AccountSettings({ user, onLogout }: { user: User; onLogout: () => Promise<void> | void }) {
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [readyEmail, setReadyEmail] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { api<{ groupReadyEmailEnabled: boolean }>('/api/settings').then((value) => setReadyEmail(value.groupReadyEmailEnabled)).catch((err) => setError(messageOf(err))); }, []);
  async function updateReadyEmail(enabled: boolean) {
    setReadyEmail(enabled);
    try { await api('/api/settings', { method: 'PATCH', body: JSON.stringify({ groupReadyEmailEnabled: enabled }) }); }
    catch (err) { setReadyEmail(!enabled); setError(messageOf(err)); }
  }
  async function remove() {
    try {
      await api('/api/account', { method: 'DELETE', body: JSON.stringify({ confirmation }), headers: { 'Content-Type': 'application/json' } });
      await onLogout();
      navigate('/');
    } catch (err) {
      setError(messageOf(err));
    }
  }
  return <div className="page narrow"><div className="page-header"><div><div className="eyebrow">账户设置</div><h1>{user.name}</h1><p>{user.email}</p></div></div><div className="settings-card"><h3>登录方式</h3><p>使用已验证邮箱和密码登录。{PASSWORD_LENGTH_HELP}</p></div><div className="settings-card"><h3>邮件通知</h3><label className="toggle-row"><input type="checkbox" checked={readyEmail} onChange={(event) => updateReadyEmail(event.target.checked)} />小组生成完成后给我发送邮件</label><p>测试保留域名地址（例如 example.com）不会触发外发邮件。</p></div>{error && <div className="form-error">{error}</div>}<div className="settings-card danger"><h3>删除账户与全部材料</h3><p>该操作将删除 D1 中的账号、小组、帖子、评论和知识状态，并删除 R2 中由你上传的原文件。</p><label>输入 DELETE 确认<input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></label><button className="button danger" disabled={confirmation !== 'DELETE'} onClick={remove}>永久删除账户</button></div></div>;
}

function AboutGroup({ meta, onUpdated }: { meta: GroupMeta; onUpdated: () => Promise<void> | void }) {
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  async function remove() {
    if (!uiConfirm(`确定删除“${meta.group.title}”及其全部材料、帖子和评论吗？`)) return;
    setDeleting(true);
    try {
      await api(`/api/groups/${meta.group.id}`, { method: 'DELETE' });
      navigate('/app');
    } catch (err) {
      uiAlert(messageOf(err));
      setDeleting(false);
    }
  }
  async function regenerate() {
    if (!uiConfirm('重新解析原始 Markdown 并生成全部知识点、帖子和评论吗？现有生成内容会被替换。')) return;
    setRegenerating(true);
    try {
      await postJson(`/api/groups/${meta.group.id}/retry`, {});
      location.reload();
    } catch (err) {
      uiAlert(messageOf(err));
      setRegenerating(false);
    }
  }
  return <><div className="rail-card"><div className="rail-title-row"><h3>关于这个小组</h3><button className="edit-personas-button" onClick={() => setEditing(true)}><Pencil size={13} /> 编辑角色</button></div><p>{meta.group.description || '由上传材料生成的私密 AI 知识小组。'}</p><div className="rail-stat"><span>模式</span><strong>{meta.group.mode === 'review' ? '汇报评审' : '知识学习'}</strong></div><div className="rail-stat"><span>AI 角色</span><strong>{meta.personas.length}</strong></div><div className="rail-stat"><span>材料</span><strong>{meta.materials.length}</strong></div><div className="persona-cloud">{meta.personas.map((persona) => <button key={persona.id} title={`编辑 ${persona.name}`} onClick={() => setEditing(true)}>{persona.name}</button>)}</div><button className="button ghost full" onClick={regenerate} disabled={regenerating || deleting}>{regenerating ? <><Loader2 className="spin" /> 正在重新解析…</> : '重新解析并生成'}</button><button className="delete-group" onClick={remove} disabled={deleting || regenerating}>{deleting ? '正在删除…' : '删除这个小组'}</button></div>{editing && <PersonaEditor groupId={meta.group.id} onClose={() => setEditing(false)} onSaved={onUpdated} />}</>;
}

function PersonaEditor({ groupId, onClose, onSaved }: { groupId: string; onClose: () => void; onSaved: () => Promise<void> | void }) {
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [draft, setDraft] = useState({ name: '', tagline: '', tone: '', systemPrompt: '' });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  useEffect(() => {
    api<{ personas: Persona[] }>(`/api/groups/${groupId}/personas`)
      .then((result) => {
        setPersonas(result.personas);
        setSelectedId(result.personas[0]?.id || '');
      })
      .catch((err) => setError(messageOf(err)))
      .finally(() => setLoading(false));
  }, [groupId]);

  const selected = personas.find((persona) => persona.id === selectedId);
  useEffect(() => {
    if (!selected) return;
    setDraft({ name: selected.name, tagline: selected.tagline, tone: selected.tone, systemPrompt: selected.system_prompt || '' });
    setSaved('');
    setError('');
  }, [selected]);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    setBusy(true);
    setError('');
    setSaved('');
    try {
      const result = await api<{ persona: Persona }>(`/api/groups/${groupId}/personas/${selected.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      setPersonas((current) => current.map((persona) => persona.id === result.persona.id ? result.persona : persona));
      setSaved('已保存');
      await onSaved();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  return <div className="persona-editor-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}><div className="persona-editor" role="dialog" aria-modal="true" aria-label="编辑小组角色"><div className="persona-editor-head"><div><h2>编辑当前小组角色</h2><p>名称和简介会立即更新；语气与角色指令用于未来的帖子和回复。</p></div><button onClick={onClose} aria-label="关闭"><X size={18} /></button></div><div className="persona-editor-body"><div className="persona-editor-list">{loading ? <PageLoader /> : personas.map((persona) => <button key={persona.id} className={persona.id === selectedId ? 'active' : ''} onClick={() => setSelectedId(persona.id)}><Avatar name={persona.name} seed={persona.avatar_seed} /><div><strong>{persona.name}</strong><span>{persona.tagline}</span></div></button>)}</div>{selected ? <form className="persona-editor-form" onSubmit={save}><label>角色名称<input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} minLength={1} maxLength={40} required /></label><label>一句话定位<input value={draft.tagline} onChange={(event) => setDraft({ ...draft, tagline: event.target.value })} minLength={2} maxLength={120} required /></label><label>表达语气<input value={draft.tone} onChange={(event) => setDraft({ ...draft, tone: event.target.value })} minLength={2} maxLength={160} required /></label><label>角色指令<textarea className="persona-prompt" value={draft.systemPrompt} onChange={(event) => setDraft({ ...draft, systemPrompt: event.target.value })} minLength={20} maxLength={1600} required /></label><div className="persona-editor-note">角色指令决定它关注什么、如何质疑以及怎样解释。材料事实仍必须来自当前小组的引用内容。</div>{error && <div className="form-error">{error}</div>}<div className="persona-editor-actions">{saved && <span className="persona-save-state">{saved}</span>}<button type="button" className="button ghost compact" onClick={onClose}>完成</button><button className="button primary compact" disabled={busy}>{busy ? <Loader2 className="spin" /> : '保存角色'}</button></div></form> : !loading && <div className="persona-editor-form"><p>当前小组没有可编辑角色。</p></div>}</div></div></div>;
}

function KnowledgePanel({ items }: { items: any[] }) {
  return <div className="rail-card"><h3>知识地图</h3>{items.slice(0, 7).map((item) => <div className="knowledge-row" key={item.id}><div><strong><RichText text={item.title} /></strong><span>{item.mastery_state === 'understood' ? '已理解' : item.mastery_state === 'review' ? '需复习' : '学习中'}</span></div><button title="标记理解" onClick={() => postJson('/api/mastery', { knowledgePointId: item.id, state: 'understood' }).then(() => location.reload())}><CheckCircle2 size={18} /></button></div>)}</div>;
}

type CommentTree = { item: Comment; children: CommentTree[] };
function CommentNode({ node, onReply, onUpdate }: { node: CommentTree; onReply: (id: string) => void; onUpdate: () => void }) {
  const comment = node.item;
  const author = comment.persona_name || comment.user_name || 'FeedDive';
  async function upvote() {
    await postJson('/api/interactions', { targetType: 'comment', targetId: comment.id, kind: 'upvote', active: !comment.upvoted });
    onUpdate();
  }
  return <div className={`comment depth-${Math.min(comment.depth, 4)}`}><div className="comment-line" /><div className="comment-main"><div className="post-meta"><Avatar name={author} seed={comment.avatar_seed} src={comment.user_avatar} /><span className="author">{author}</span>{comment.source_type === 'ai' && <span className="ai-badge"><Sparkles size={11} /> AI</span>}<span>· {timeAgo(comment.created_at)}</span></div><RichText as="p" text={comment.body} />{comment.citations?.length > 0 && <CitationList citations={comment.citations} />}<div className="comment-actions"><button className={comment.upvoted ? 'active' : ''} onClick={upvote}><ArrowBigUp size={16} />{comment.score}</button><button onClick={() => onReply(comment.id)}><MessageCircle size={15} />回复</button><button onClick={() => reportContent('comment', comment.id)}><Flag size={14} />举报</button></div>{node.children.map((child) => <CommentNode key={child.item.id} node={child} onReply={onReply} onUpdate={onUpdate} />)}</div></div>;
}

function AdminReports({ user }: { user: User }) {
  const [reports, setReports] = useState<any[]>([]);
  const [status, setStatus] = useState('open');
  const [error, setError] = useState('');
  const load = useCallback(() => api<{ reports: any[] }>(`/api/admin/reports?status=${encodeURIComponent(status)}`).then((result) => { setReports(result.reports); setError(''); }).catch((err) => setError(messageOf(err))), [status]);
  useEffect(() => { if (user.role === 'admin') load(); }, [load, user.role]);
  async function review(id: string, next: 'reviewing' | 'resolved' | 'dismissed') {
    try { await api(`/api/admin/reports/${id}`, { method: 'PATCH', body: JSON.stringify({ status: next }) }); await load(); }
    catch (err) { setError(messageOf(err)); }
  }
  if (user.role !== 'admin') return <ErrorState message="需要管理员权限。" />;
  return <div className="page"><div className="page-header"><div><div className="eyebrow">运营安全</div><h1>内容举报队列</h1><p>按提交时间处理，并保留审核状态和审计记录。</p></div><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="open">待处理</option><option value="reviewing">审核中</option><option value="resolved">已处理</option><option value="dismissed">不成立</option><option value="all">全部</option></select></div>{error && <div className="form-error">{error}</div>}{reports.length === 0 ? <div className="empty-card"><CheckCircle2 /><h3>当前没有此状态的举报</h3></div> : <div className="report-list">{reports.map((report) => <article key={report.id} className="report-card"><header><strong>{report.reason}</strong><span>{report.status}</span><time>{timeAgo(report.created_at)}</time></header><p><b>{report.group_title}</b> · {report.target_type} / {report.target_id}</p><blockquote>{report.target_excerpt || '目标内容已删除'}</blockquote>{report.details && <p>举报说明：{report.details}</p>}<small>提交者：{report.reporter_email}</small><div><button className="button ghost compact" onClick={() => review(report.id, 'reviewing')}>标记审核中</button><button className="button primary compact" onClick={() => review(report.id, 'resolved')}>已处理</button><button className="button ghost compact" onClick={() => review(report.id, 'dismissed')}>不成立</button></div></article>)}</div>}</div>;
}

function LegalPage({ kind }: { kind: 'privacy' | 'terms' | 'acceptable' | 'support' }) {
  const updated = '2026-09-25';
  const content = {
    privacy: <><h1>隐私说明</h1><p>更新日期：{updated}</p><h2>我们处理什么</h2><p>账号资料、会话安全信息、你上传的原文件、解析文本、生成内容、互动、用量和必要的安全审计记录。</p><h2>材料去向</h2><p>Cloudflare Workers、D1、R2、Queues、Workers AI、Vectorize 和 Markdown Conversion 用于存储与处理。Chat 生成首先使用 OpenRouter 的 StreamLake 限定路由运行 DeepSeek V4 Flash 0731，并禁用 OpenRouter 内部 provider fallback；OpenRouter 当前披露 StreamLake 不使用 prompts 训练模型、但可能保留 prompts，且总部在中国。材料片段、提示词和完成生成所需的指令可能由 OpenRouter 与 StreamLake 按其适用条款处理和保留。只有可重试的供应商故障时才由 FeedDive 切换到 DeepInfra，并可能发送同一生成输入。LiteLLM 与 OpenCode 不属于当前 Production Chat 处理链。音频仅由 Cloudflare Workers AI Whisper 处理。</p><h2>重要限制</h2><p>免费模型端点不适合机密、受监管或可识别个人的信息。请勿上传密码、密钥、医疗或金融记录、未公开商业资料，或你无权处理的第三方内容。</p><h2>保留与删除</h2><p>数据保留到你删除小组或账号；删除入口会移除应用数据库、向量和原始 R2 对象。基础设施提供商的短期日志、缓存或备份仍受各提供商规则约束。</p><h2>联系</h2><p>支持与数据请求可发送至 support@feeddive.catoracletech.com。请勿通过邮件发送密码、密钥或完整私人材料。</p></>,
    terms: <><h1>服务条款</h1><p>更新日期：{updated}</p><p>FeedDive 用 AI 把获授权材料变成学习或汇报评审讨论。输出可能错误、不完整或带有偏见，不能替代专业、学术、法律、医疗或财务判断。</p><h2>你的责任</h2><p>你必须拥有上传和处理材料的权限，核对重要输出和引用，并遵守学校、雇主、版权及隐私规则。不得把示例答案冒充未经允许的原创成果。</p><h2>服务状态</h2><p>第三方 AI 服务可能限流、中断或调整可用性。任务失败时会显示状态并允许安全重试；FeedDive 不会在活动 provider chain 外静默切换到未批准模型。</p><h2>付费</h2><p>付费订阅由 Dodo Payments 处理；结账前会展示产品、价格和续订周期，最终金额以 Dodo Payments 结账页为准。Pro 为 $9.99/月，Premium 为 $19.99/月，目前不提供年付方案。</p><h2>终止</h2><p>滥用、违法、侵权或危害系统安全的账号可被限制。你可在设置中删除账号。</p></>,
    acceptable: <><h1>可接受使用规则</h1><p>不得上传或生成违法、侵权、仇恨骚扰、性剥削、自伤诱导、恶意代码、凭证窃取、隐私侵害或学术作弊内容；不得绕过配额、批量抓取、攻击服务或冒充他人。</p><p>发现问题可使用每条帖子与评论旁的“举报”。管理员可将举报标记为审核中、已处理或不成立。</p></>,
    support: <><h1>支持与数据请求</h1><p>应用内失败页面提供安全重试；AI 回复失败时，原始真人内容会保留，并显示独立任务状态。</p><p>支持与数据请求可发送至 support@feeddive.catoracletech.com。紧急安全问题请勿附带真实用户材料或生产密钥。</p><h2>提交时请包含</h2><p>发生时间、页面路径、请求 ID（如有）、复现步骤和脱敏截图。不要发送密码、会话 Cookie、API Key 或完整私人材料。</p></>,
  }[kind];
  return <div className="legal-shell"><header><Link to="/"><Brand /></Link><Link className="button ghost compact" to="/">返回首页</Link></header><article>{content}<nav><Link to="/privacy">隐私</Link><Link to="/terms">条款</Link><Link to="/acceptable-use">可接受使用</Link><Link to="/support">支持</Link></nav></article></div>;
}

function buildCommentTree(comments: Comment[]): CommentTree[] {
  const map = new Map<string, CommentTree>();
  comments.forEach((item) => map.set(item.id, { item, children: [] }));
  const roots: CommentTree[] = [];
  for (const node of map.values()) {
    if (node.item.parent_id && map.has(node.item.parent_id)) map.get(node.item.parent_id)!.children.push(node);
    else roots.push(node);
  }
  return roots;
}

function CitationList({ citations }: { citations: Citation[] }) {
  const [open, setOpen] = useState(false);
  return <div className="citations"><button onClick={() => setOpen(!open)}><FileText size={14} />{citations.length} 条材料引用 <ChevronDown className={open ? 'rotated' : ''} /></button>{open && <div className="citation-list">{citations.map((citation, index) => <blockquote key={`${citation.chunk_id}-${index}`}><strong>{citation.material_name}</strong><RichText as="p" text={citation.quote} /></blockquote>)}</div>}</div>;
}

async function reportContent(targetType: 'post' | 'comment', targetId: string) {
  const details = uiPrompt('请简要说明问题（可选）。不要填写密码、密钥或其他敏感信息。');
  if (details === null) return;
  try {
    await postJson('/api/reports', { targetType, targetId, reason: 'other', details });
    uiAlert('举报已提交，管理员会在运营队列中处理。');
  } catch (error) {
    uiAlert(messageOf(error));
  }
}

function GroupCard({ group }: { group: any }) {
  return <Link className="group-card" to={`/g/${group.id}`}><div className={`group-card-icon ${group.mode}`}>{group.mode === 'review' ? <Flag /> : <BookOpen />}</div><div className="group-card-top"><span>{group.mode === 'review' ? '评审小组' : '学习小组'}</span>{group.status === 'processing' && <span className="status processing"><Loader2 className="spin" />生成中</span>}{group.status === 'failed' && <span className="status failed">失败</span>}</div><h3>{group.title}</h3><p>{group.description || '围绕材料生成的多角色知识讨论。'}</p><div className="group-card-foot"><span><FileText size={15} />{group.material_count} 份材料</span><span><MessageCircle size={15} />{group.post_count} 帖子</span><span>{timeAgo(group.created_at)}</span></div></Link>;
}
function EmptyDashboard() { return <div className="empty-dashboard"><div><BrainCircuit /></div><h2>从一份材料开始</h2><p>上传课程笔记、论文或汇报稿，FeedDive 会创建角色、知识点、帖子和引用。</p><Link className="button primary" to="/app/new"><Plus size={17} /> 创建第一个小组</Link></div>; }
function ProcessingState({ title, mode }: { title: string; mode: string }) { return <div className="processing-page"><div className="processing-orbit"><BrainCircuit /><span /><span /><span /></div><div className="eyebrow">{mode === 'review' ? '正在召集评审' : '正在组建学习小组'}</div><h1>{title}</h1><p>解析材料、建立知识点、创建角色，并让不同角色围绕每个帖子展开回复。页面会自动刷新。</p><div className="process-list"><span className="done"><CheckCircle2 />材料已解析</span><span><Loader2 className="spin" />生成知识点和公式内容</span><span><Clock3 />创建多角色帖子与评论</span></div></div>; }
function ErrorState({ message, retry }: { message: string; retry?: () => void }) { return <div className="empty-dashboard error-state"><div><CircleAlert /></div><h2>没有成功完成</h2><p>{message}</p>{retry && <button className="button primary" onClick={retry}>重新检查</button>}</div>; }
function NotFound() { return <div className="not-found"><Brand /><h1>404</h1><p>这个讨论可能已经被删除，或者你没有访问权限。</p><Link className="button primary" to="/app">返回主页</Link></div>; }
function FullScreenLoader({ label }: { label: string }) { return <div className="full-loader"><Brand /><Loader2 className="spin" /><span>{label}</span></div>; }
function PageLoader() { return <div className="page-loader"><Loader2 className="spin" /><span>加载讨论</span></div>; }
function Brand() { return <div className="brand"><div className="brand-mark">F<span /></div><strong>FeedDive</strong></div>; }
function getAvatarInitials(name: string, seed?: string): string {
  const isEn = uiLocale() === 'en';
  const localized = uiText(name).trim();
  if (isEn) {
    const cleaned = localized.replace(/[^a-zA-Z0-9\s]/g, '').trim();
    if (cleaned) {
      const parts = cleaned.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return cleaned.slice(0, 2).toUpperCase();
    }
    const seedLetters = (seed || '').replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase();
    if (seedLetters) return seedLetters;
    const fallbackLetters = ['AI', 'FD', 'U', 'P', 'R', 'K'];
    return fallbackLetters[hashSeed(seed || name) % fallbackLetters.length];
  }
  return localized.slice(0, 2).toUpperCase();
}
function Avatar({ name, src, seed }: { name: string; src?: string | null; seed?: string }) { if (src) return <img className="avatar" src={src} alt="" referrerPolicy="no-referrer" />; const initials = getAvatarInitials(name, seed); return <span className={`avatar generated seed-${hashSeed(seed || name) % 8}`}>{initials}</span>; }
function NavLink({ to, active, icon, children }: { to: string; active: boolean; icon: ReactNode; children: ReactNode }) { return <Link to={to} className={active ? 'nav-link active' : 'nav-link'}>{icon}<span>{children}</span></Link>; }
function Metric({ icon, value, label }: { icon: ReactNode; value: number; label: string }) { return <div className="metric"><div>{icon}</div><strong>{value}</strong><span>{label}</span></div>; }
function MockPost({ role, title, body, score, seed }: { role: string; title: string; body: string; score: number; seed?: string }) { const roleText = uiText(role); return <div className="mock-post"><div className="mock-votes"><ArrowBigUp /><strong>{score}</strong></div><div><div className="post-meta"><Avatar name={roleText} seed={seed || role} /><strong>{roleText}</strong><span className="ai-badge">AI</span></div><h3>{title}</h3><p>{body}</p><span className="mock-comments"><MessageCircle /> 讨论正在展开</span></div></div>; }
function Step({ n, icon, title, text }: { n: string; icon: ReactNode; title: string; text: string }) { return <div className="step"><span>{n}</span><div>{icon}</div><h3>{title}</h3><p>{text}</p></div>; }
function ModeCard({ type, title, items }: { type: string; title: string; items: string[] }) { return <div className="mode-card"><span>{type}</span><h3>{title}</h3><ul>{items.map((item) => <li key={item}><CheckCircle2 />{item}</li>)}</ul></div>; }
function uiLocale() { return (window as any).__FEEDDIVE_UI_LOCALE__?.locale === 'zh-CN' ? 'zh-CN' : 'en'; }
function uiText(value: string) { return String((window as any).__FEEDDIVE_UI_TEXT__?.(value) ?? value); }
function uiConfirm(value: string) { return window.confirm(uiText(value)); }
function uiAlert(value: string) { window.alert(uiText(value)); }
function uiPrompt(value: string) { return window.prompt(uiText(value)); }
function timeAgo(value: string) { try { return formatDistanceToNow(new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`), { addSuffix: true, locale: uiLocale() === 'zh-CN' ? zhCN : enUS }); } catch { return value; } }
function messageOf(error: unknown) { const message = error instanceof ApiError || error instanceof Error ? error.message : '发生未知错误。'; return uiLocale() === 'en' && /[\u3400-\u9fff]/.test(message) ? 'Something went wrong. Please try again.' : uiText(message); }
function hashSeed(value: string) { let hash = 0; for (let index = 0; index < value.length; index += 1) hash = (hash * 31 + value.charCodeAt(index)) | 0; return Math.abs(hash); }
